import React from 'react';
import { NavLink, Outlet, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import {
  LayoutDashboard,
  Users,
  Puzzle,
  KeyRound,
  LogOut,
  ExternalLink,
  ChevronRight,
} from 'lucide-react';

const AdminLayout = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const isAdmin = user?.role === 'ADMIN';

  const handleLogout = async () => {
    await logout();
    navigate('/admin/login');
  };

  const navItems = [
    { to: '/admin/dashboard', label: 'Dashboard', icon: LayoutDashboard, roles: ['ADMIN', 'STAFF'] },
    { to: '/admin/users', label: 'Users', icon: Users, roles: ['ADMIN'] },
    { to: '/admin/quote-builder', label: 'Quote Builder', icon: Puzzle, roles: ['ADMIN'] },
    { to: '/admin/change-password', label: 'Change Password', icon: KeyRound, roles: ['ADMIN', 'STAFF'] },
  ];

  const visibleItems = navItems.filter((item) => item.roles.includes(user?.role));

  return (
    <div className="flex h-screen overflow-hidden bg-[#F5F5F0]" data-testid="admin-layout">
      {/* Sidebar */}
      <aside className="w-[260px] flex-shrink-0 bg-[#1A1A1A] flex flex-col" data-testid="admin-sidebar">
        {/* Branding */}
        <div className="px-6 py-6 border-b border-white/10">
          <h2
            className="text-lg font-bold text-white tracking-wide"
            style={{ fontFamily: 'Playfair Display, serif' }}
          >
            A. Loizides & Sons
          </h2>
          <p className="text-xs text-[#C69C6D] mt-1 font-medium tracking-wider uppercase">
            Admin Panel
          </p>
        </div>

        {/* Navigation */}
        <nav className="flex-1 px-3 py-4 space-y-1 overflow-y-auto">
          {visibleItems.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
              className={({ isActive }) =>
                `group flex items-center gap-3 px-4 py-3 rounded-lg text-sm font-medium transition-all duration-200 ${
                  isActive
                    ? 'bg-[#C69C6D]/15 text-[#C69C6D]'
                    : 'text-[#A0A0A0] hover:text-white hover:bg-white/5'
                }`
              }
            >
              {({ isActive }) => (
                <>
                  <item.icon size={18} strokeWidth={isActive ? 2.2 : 1.8} />
                  <span className="flex-1">{item.label}</span>
                  {isActive && (
                    <ChevronRight size={14} className="text-[#C69C6D]/60" />
                  )}
                </>
              )}
            </NavLink>
          ))}
        </nav>

        {/* Bottom section */}
        <div className="border-t border-white/10 p-4 space-y-3">
          <a
            href="/"
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-2 px-4 py-2 text-xs text-[#808080] hover:text-[#C69C6D] transition-colors"
            data-testid="view-website-link"
          >
            <ExternalLink size={14} />
            View Website
          </a>

          {/* User info */}
          <div className="px-4 py-3 bg-white/5 rounded-lg">
            <p className="text-sm font-medium text-white truncate">
              {user?.name || user?.email}
            </p>
            <p className="text-xs text-[#808080] mt-0.5">
              {user?.role === 'ADMIN' ? 'Administrator' : 'Staff'}
            </p>
          </div>

          <button
            onClick={handleLogout}
            className="flex items-center gap-2 w-full px-4 py-2.5 text-sm text-[#808080] hover:text-red-400 hover:bg-red-500/10 rounded-lg transition-all duration-200"
            data-testid="sidebar-logout-button"
          >
            <LogOut size={16} />
            Sign Out
          </button>
        </div>
      </aside>

      {/* Main Content */}
      <main className="flex-1 overflow-y-auto" data-testid="admin-main-content">
        <Outlet />
      </main>
    </div>
  );
};

export default AdminLayout;
